import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { ApiError, apiGet, apiSend } from '../lib/api'
import { supabase } from '../lib/supabase'
import type { Achievements, Me } from '../lib/types'

// Смена экзамена живёт на экране «Цель и прогресс» (Progress.tsx) — там же,
// где и в боте (goal.py, кнопка «🔀 Экзамен: X»), а не здесь.

/**
 * Достижения — веб-версия _format_achievements (app/handlers/navigation.py):
 * уровень, опыт, значки, дословно та же структура и та же нижняя строка
 * с кнопками «🎯 Цель и прогресс» / «📜 Правила».
 *
 * У бота нет входа с паролем, поэтому аккаунтных вещей (выход, привязка
 * Telegram, экзамен) на этом экране в боте тоже нет — здесь они внизу,
 * отдельным блоком, как единственное необходимое отступление от бота.
 */
// Откуда пришли на экран — в адресе (?from=goal). navigate(-1) для этого не
// годится: история браузера помнит не только переходы по кнопкам, и «назад»
// уводило бы то на экран задачи, то вообще с сайта — а при заходе по прямой
// ссылке возвращать было бы некуда. В боте эта же связь решена источником в
// callback_data (_ACH_BACK в navigation.py).
const ОТКУДА: Record<string, { подпись: string; путь: string }> = {
  goal: { подпись: '🎯 Цель и прогресс', путь: '/progress' },
  help: { подпись: '❓ Помощь', путь: '/help' },
  rules: { подпись: '📜 Правила', путь: '/rules' },
}

export default function AchievementsScreen({ me }: { me: Me }) {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const назад = ОТКУДА[params.get('from') ?? '']
  const queryClient = useQueryClient()
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')

  const { data: ach, isLoading } = useQuery<Achievements>({
    queryKey: ['achievements'],
    queryFn: () => apiGet('/api/me/achievements'),
  })

  async function привязать() {
    setBusy(true)
    setError('')
    setNotice('')
    try {
      await apiSend('/api/me/link-telegram', 'POST', { code: code.trim().toUpperCase() })
      setNotice('Готово! Прогресс из бота теперь здесь.')
      setCode('')
      await queryClient.invalidateQueries()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось привязать.')
    } finally {
      setBusy(false)
    }
  }

  if (isLoading || !ach) {
    return (
      <>
        <h1>🏆 Достижения</h1>
        <div className="skeleton" />
        <div className="skeleton" />
      </>
    )
  }

  const точность =
    ach.tasks_solved > 0 ? `${ach.accuracy_pct}% (${ach.tasks_correct}/${ach.tasks_solved})` : 'пока нет задач'

  return (
    <>
      <h1>🏆 Достижения</h1>

      <div className="card">
        <div className="row">
          <b>Уровень</b>
          <span className="spacer" />
          <span className="badge">{ach.level_label}</span>
        </div>
        {ach.level_xp_total ? (
          <>
            <div className="bar" style={{ margin: '10px 0 6px' }}>
              <i style={{ width: `${Math.round((ach.level_xp / ach.level_xp_total) * 100)}%` }} />
            </div>
            <p className="small muted" style={{ margin: 0 }}>
              {ach.level_xp} / {ach.level_xp_total} XP до следующего уровня
            </p>
          </>
        ) : (
          <p className="small muted" style={{ margin: 0 }}>🏆 Максимальный уровень</p>
        )}

        <p style={{ margin: '12px 0 0' }}>
          {/* tasks_solved считает и неверные ответы: «решено 11» при 8 верных
              вводило в заблуждение. Решено — это верные, как и в боте. */}
          <b>Задач решено:</b> {ach.tasks_correct} из {ach.tasks_solved}
        </p>
        <p style={{ margin: '4px 0 0' }}>
          <b>Точность:</b> {точность}
        </p>

        {/* Значки списком с названиями: прежде были одни иконки, а название
            пряталось в подсказку при наведении — на телефоне его не увидеть.
            Неполученные показаны бледно, с условием: видно, к чему идти. */}
        {ach.badges_all.length > 0 && (
          <div style={{ margin: '12px 0 0' }}>
            <b>Значки</b>
            {ach.badges_all.map((b) => (
              <p
                key={b.id}
                className={b.earned ? undefined : 'muted'}
                style={{ margin: '6px 0 0', opacity: b.earned ? 1 : 0.6 }}
              >
                <span aria-hidden>{b.icon}</span> {b.title}
                {b.earned ? ' ✅' : <span className="small"> — {b.description}</span>}
              </p>
            ))}
          </div>
        )}
      </div>

      {error && <div className="error">{error}</div>}
      {notice && <div className="badge badge-ok">{notice}</div>}

      <div className="row-wrap" style={{ marginTop: 4, marginBottom: 20 }}>
        {/* Источник известен — «Назад» с именем экрана; неизвестен (прямая
            ссылка) — ведём на цель, чтобы экран не оказался тупиком. */}
        {назад ? (
          <button className="btn" onClick={() => navigate(назад.путь)}>
            ⬅️ Назад — {назад.подпись}
          </button>
        ) : (
          <button className="btn" onClick={() => navigate('/progress')}>
            🎯 Цель и прогресс
          </button>
        )}
        {params.get('from') !== 'rules' && (
          <button className="btn" onClick={() => navigate('/rules')}>📜 Правила</button>
        )}
        <button className="btn" onClick={() => navigate('/')}>🏠 Главная</button>
      </div>

      {/* В боте такого блока нет — там не нужен выход из аккаунта, пароля не
          бывает. Здесь эта вещь неизбежна технически, поэтому вынесена
          отдельно, ниже игровой части. */}
      <h2>Аккаунт</h2>

      <div className="card">
        <b>Занятия в Telegram</b>
        {me.linked_telegram ? (
          <p className="muted" style={{ margin: '8px 0 0' }}>
            ✅ Привязано — прогресс общий, где бы ты ни занимался.
          </p>
        ) : (
          <>
            <p className="muted small" style={{ margin: '8px 0' }}>
              Занимаешься в боте? Напиши ему <code>/web</code>, получи код и введи его
              здесь — история, цель и очки станут общими.
            </p>
            <div className="composer-row">
              <input
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="Код из бота"
                maxLength={16}
                autoCapitalize="characters"
              />
              <button className="btn btn-primary" onClick={привязать} disabled={busy || code.length < 4}>
                Привязать
              </button>
            </div>
          </>
        )}
      </div>

      <button
        className="btn btn-block"
        style={{ marginTop: 16 }}
        onClick={() => supabase.auth.signOut()}
      >
        Выйти
      </button>
    </>
  )
}
